<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import { CLOUD_STORAGE } from "../../../core/constants.js";
import { useAddModal } from "../../composables/useAddModal.js";
import ProjectService, { PROJECT_CONSTANT } from "../../services/projectService.js";
import { useProjectsByTab } from "../../composables/useProjectsByTab.js";
import { useDocumentUpload } from "../../composables/useDocumentUpload.js";
import { useCloudStorage } from "../../composables/useCloudStorage.js";
import CloudStorageFinder from "./CloudStorageFinder.vue";
import AddStorageModal from "./AddStorageModal.vue";
import RenameModal from "./RenameModal.vue";
import ConfirmModal from "./ConfirmModal.vue";

const route = useRoute();
const { activeTab, contextProjectId, close } = useAddModal();
const projectService = new ProjectService();
const { refreshAll: refreshProjectsList } = useProjectsByTab();
const { uploadDocuments } = useDocumentUpload();
const {
  storages: cloudStorages, isLoading: isLoadingStorages, pendingProvider, AUTHENTICATION_STATUS,
  load: loadCloudStorages, connect: connectCloudStorage, rename: renameCloudStorage,
  remove: removeCloudStorage, clearPendingProvider,
} = useCloudStorage();

const projects = ref([]);
const isLoadingProjects = ref(false);

async function loadProjects() {
  isLoadingProjects.value = true;
  try {
    const res = await projectService.getProjects({ projectType: PROJECT_CONSTANT.PROJECT_TYPES.ALL });
    projects.value = res.projects ?? [];
  } finally {
    isLoadingProjects.value = false;
  }
}

// ─── "Add to" selection ────────────────────────────────────────────────────
// "documents" = add to doc list (no project), otherwise a project id
const selectedId = ref("documents");

onMounted(async () => {
  // Explicit context (opened via "Add document") wins; otherwise, if the
  // modal was opened while viewing a project (e.g. the "+" button defaults
  // to "Create Project" there), still default the picker to that project so
  // switching to "Add document" doesn't fall back to the unfiled bucket.
  const currentProjectId = route.name === "v2-project" ? route.params.projectId : null;
  selectedId.value = contextProjectId.value ?? currentProjectId ?? "documents";
  document.addEventListener("keydown", onEsc);
  loadProjects();

  await loadCloudStorages();
  // Returning from a cloud storage OAuth redirect — auto-open its file
  // picker, mirroring the legacy app's own localStorage handoff.
  if (pendingProvider.value) {
    const storage = cloudStorages.value.find((s) => s.provider === pendingProvider.value);
    if (storage) selectedStorage.value = storage;
    clearPendingProvider();
  }
});
onBeforeUnmount(() => document.removeEventListener("keydown", onEsc));

function onEsc(e) { if (e.key === "Escape") close(); }

const selectedLabel = computed(() => {
  if (selectedId.value === "documents") return "Documents";
  return projects.value.find((p) => p.id === selectedId.value)?.name ?? "Documents";
});

// ─── Custom dropdown ───────────────────────────────────────────────────────
const showDropdown = ref(false);

function selectProject(id) {
  selectedId.value = id;
  showDropdown.value = false;
}
function selectDocuments() {
  selectedId.value = "documents";
  showDropdown.value = false;
}
function selectCreateProject() {
  showDropdown.value = false;
  returnToDocOnSuccess.value = true;
  activeTab.value = "project";
}

// ─── Create project state ──────────────────────────────────────────────────
const returnToDocOnSuccess = ref(false); // did user arrive here from "Add document"?
const projectName = ref("");
const projectDescription = ref("");
const isCreating = ref(false);
const createError = ref(false);

async function createProject() {
  const name = projectName.value.trim();
  if (!name || isCreating.value) return;
  isCreating.value = true;
  createError.value = false;
  try {
    await projectService.createProject({ name, description: projectDescription.value.trim() });

    // The createProject mutation doesn't return the new project's id, so
    // refresh the list and match by name to pick it up in the dropdown.
    await loadProjects();
    const created = projects.value.find((p) => p.name === name);

    if (returnToDocOnSuccess.value) {
      selectedId.value = created?.id ?? "documents";
      activeTab.value = "document";
      returnToDocOnSuccess.value = false;
    } else {
      close();
    }
    // Silently sync the Projects page's cached lists in the background —
    // no reload, no visible flash — so the new project just shows up.
    refreshProjectsList();
    projectName.value = "";
    projectDescription.value = "";
  } catch (error) {
    createError.value = true;
  } finally {
    isCreating.value = false;
  }
}

// ─── Cloud storage sources ──────────────────────────────────────────────────
const selectedStorage = ref(null); // opens CloudStorageFinder when set
const showAddStorageModal = ref(false);
const openMenuFor = ref(null); // storage id whose kebab menu is open
const renamingStorage = ref(null);
const isRenamingStorage = ref(false);
const removingStorage = ref(null);
const isRemovingStorage = ref(false);

function targetProjectId() {
  return selectedId.value === "documents" ? null : selectedId.value;
}

function onStorageClick(storage) {
  if (storage.authenticationStatus === AUTHENTICATION_STATUS.TOKEN_EXPIRED) {
    connectCloudStorage(storage.provider, storage);
    return;
  }
  selectedStorage.value = storage;
}

function onRenameClick(storage) { openMenuFor.value = null; renamingStorage.value = storage; }
async function saveStorageRename({ name }) {
  isRenamingStorage.value = true;
  try {
    await renameCloudStorage(renamingStorage.value, name);
    renamingStorage.value = null;
  } finally {
    isRenamingStorage.value = false;
  }
}

function onRemoveClick(storage) { openMenuFor.value = null; removingStorage.value = storage; }
async function confirmRemoveStorage() {
  isRemovingStorage.value = true;
  try {
    const removedId = removingStorage.value.id;
    await removeCloudStorage(removingStorage.value);
    removingStorage.value = null;
    if (selectedStorage.value?.id === removedId) selectedStorage.value = null;
  } finally {
    isRemovingStorage.value = false;
  }
}

// ─── File input ────────────────────────────────────────────────────────────
const fileInputRef = ref(null);
function triggerFile() { fileInputRef.value?.click(); }

function onFilesPicked(e) {
  const picked = e.target.files;
  if (picked?.length) {
    // "documents" means no project was chosen — useDocumentUpload treats a
    // null projectId as the unfiled-documents bucket (projectId: "SINGLETON").
    const projectId = selectedId.value === "documents" ? null : selectedId.value;
    uploadDocuments(projectId, picked);
    close();
  }
  e.target.value = ""; // allow re-picking the same file later
}
</script>

<template>
  <!-- Backdrop -->
  <div
    class="fixed inset-0 bg-black/20 dark:bg-black/50 z-[400] flex items-center justify-center p-4"
    @click.self="close"
  >
    <!-- Dropdown click-away overlay (sits behind dropdown, above rest of modal) -->
    <div
      v-if="showDropdown"
      class="fixed inset-0 z-[410]"
      @click="showDropdown = false"
    />

    <!-- Modal card -->
    <div class="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[540px] flex flex-col overflow-visible z-[420]">

      <!-- ── Tab header ── -->
      <div class="flex items-center gap-3 p-4 shrink-0">
        <div class="flex items-center gap-1 flex-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
          <button
            :class="activeTab === 'document'
              ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-100'
              : 'bg-transparent text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'"
            class="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all border-0 cursor-pointer flex-1 justify-center"
            @click="activeTab = 'document'; returnToDocOnSuccess = false"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
            </svg>
            Add document
          </button>
          <button
            :class="activeTab === 'project'
              ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-100'
              : 'bg-transparent text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'"
            class="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all border-0 cursor-pointer flex-1 justify-center"
            @click="activeTab = 'project'"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8" />
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="currentColor" stroke-width="1.8" />
            </svg>
            Create Project
          </button>
        </div>
        <button
          class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors shrink-0"
          @click="close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div class="h-px bg-slate-200 dark:bg-slate-700 shrink-0" />

      <!-- ══ Add Document tab ══════════════════════════════════════════════ -->
      <div v-if="activeTab === 'document'" class="p-5 flex flex-col gap-4" @click="openMenuFor = null">

        <!-- Add to — custom dropdown -->
        <div class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl px-4 py-3 flex items-center gap-3">
          <span class="text-sm text-slate-400 dark:text-slate-500 shrink-0">Add to :</span>

          <!-- Trigger -->
          <div class="relative flex-1">
            <button
              :class="showDropdown
                ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-900'
                : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'"
              class="flex items-center justify-between w-full bg-white dark:bg-slate-700 border rounded-xl px-3 py-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer transition-all text-left"
              @click.stop="showDropdown = !showDropdown"
            >
              <span>{{ selectedLabel }}</span>
              <svg class="text-slate-400 shrink-0 ml-2" width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>

            <!-- Dropdown panel -->
            <div
              v-if="showDropdown"
              class="absolute top-[calc(100%+6px)] left-0 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl py-2 z-[430] overflow-hidden"
              @click.stop
            >
              <!-- Projects section -->
              <p class="text-[11px] text-slate-400 dark:text-slate-500 px-3 pt-1 pb-1 m-0 uppercase tracking-wide font-semibold">Projects</p>
              <p v-if="isLoadingProjects" class="text-xs text-slate-400 dark:text-slate-500 px-3 py-2 m-0">Loading projects…</p>
              <template v-else>
                <button
                  v-for="p in projects"
                  :key="p.id"
                  :class="selectedId === p.id
                    ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400'
                    : 'text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700/60'"
                  class="flex items-center w-full px-3 py-2.5 text-sm border-0 bg-transparent cursor-pointer transition-colors text-left"
                  @click="selectProject(p.id)"
                >
                  {{ p.name }}
                </button>
                <p v-if="!projects.length" class="text-xs text-slate-400 dark:text-slate-500 px-3 py-2 m-0">No projects yet</p>
              </template>

              <!-- Documents option -->
              <div class="mx-3 my-1.5 h-px bg-slate-100 dark:bg-slate-700" />
              <button
                :class="selectedId === 'documents'
                  ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400'
                  : 'text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700/60'"
                class="flex items-center w-full px-3 py-2.5 text-sm border-0 bg-transparent cursor-pointer transition-colors text-left"
                @click="selectDocuments"
              >
                Documents
              </button>

              <!-- Create Project + -->
              <button
                class="flex items-center justify-between w-full px-3 py-2.5 text-sm text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700/60 border-0 bg-transparent cursor-pointer transition-colors"
                @click="selectCreateProject"
              >
                <span>Create Project</span>
                <svg class="text-slate-400" width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Local files -->
        <div
          class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 flex items-center gap-4 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/70 transition-colors"
          @click="triggerFile"
        >
          <div class="flex items-center justify-center w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-950 shrink-0">
            <svg class="text-blue-500" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div>
            <p class="text-xs text-slate-400 dark:text-slate-500 m-0 mb-0.5">Add from</p>
            <p class="text-sm font-semibold text-slate-800 dark:text-slate-100 m-0">Local Files</p>
          </div>
        </div>
        <!-- No accept filter — any file type is allowed. The backend decides
             annotatability (isAnnotatable); non-annotatable uploads still
             land here as "download-only" files, same as the legacy app. -->
        <input
          ref="fileInputRef"
          type="file"
          class="hidden"
          multiple
          @change="onFilesPicked"
        />

        <!-- Connected cloud sources -->
        <div class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4">
          <div class="flex items-center justify-between mb-3">
            <span class="text-sm font-medium text-slate-500 dark:text-slate-400">Connected cloud sources</span>
            <button
              class="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600 dark:text-blue-400 border-0 bg-transparent cursor-pointer font-medium"
              @click="showAddStorageModal = true"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
              </svg>
              Add new
            </button>
          </div>

          <p v-if="isLoadingStorages" class="text-xs text-slate-400 dark:text-slate-500 m-0">Loading storages…</p>
          <p v-else-if="!cloudStorages.length" class="text-xs text-slate-400 dark:text-slate-500 m-0">No cloud storage connected yet</p>
          <div v-else class="grid grid-cols-2 gap-2">
            <div
              v-for="storage in cloudStorages"
              :key="storage.id"
              class="relative flex items-center gap-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl p-3 cursor-pointer hover:border-slate-300 dark:hover:border-slate-500 transition-colors"
              @click="onStorageClick(storage)"
            >
              <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-slate-50 dark:bg-slate-600 shrink-0">
                <svg v-if="storage.provider === CLOUD_STORAGE.GOOGLE_DRIVE" viewBox="0 0 16 16" width="20" height="20" fill="none">
                  <path d="M1.20938 13.1039L1.91563 14.3227C2.0625 14.5789 2.27188 14.782 2.52188 14.9289L5.04063 10.5664H0C0 10.8508 0.071875 11.1352 0.21875 11.3914L1.20938 13.1039Z" fill="#0066DA"/>
                  <path d="M8 5.43281L5.48125 1.07031C5.23438 1.21719 5.02187 1.41719 4.875 1.67656L0.21875 9.73906C0.075 9.98906 0 10.2734 0 10.5641H5.04063L8 5.43281Z" fill="#00AC47"/>
                  <path d="M13.4809 14.9289C13.7277 14.782 13.9402 14.582 14.0871 14.3227L14.3809 13.8195L15.784 11.3914C15.9309 11.1352 16.0027 10.8508 16.0027 10.5664H10.959L12.0309 12.6727L13.4809 14.9289Z" fill="#EA4335"/>
                  <path d="M7.99922 5.43281L10.518 1.07031C10.2711 0.923437 9.98672 0.851562 9.69297 0.851562H6.30547C6.01172 0.851562 5.72734 0.932812 5.48047 1.07031L7.99922 5.43281Z" fill="#00832D"/>
                  <path d="M10.9602 10.5664H5.04141L2.51953 14.9289C2.76641 15.0758 3.05078 15.1477 3.34453 15.1477H12.657C12.9508 15.1477 13.2352 15.0664 13.482 14.9289L10.9602 10.5664Z" fill="#2684FC"/>
                  <path d="M13.4531 5.70781L11.125 1.67656C10.9781 1.42031 10.7687 1.21719 10.5187 1.07031L8 5.43281L10.9594 10.5641H15.9906C15.9906 10.2797 15.9187 9.99531 15.7719 9.73906L13.4531 5.70781Z" fill="#FFBA00"/>
                </svg>
                <svg v-else-if="storage.provider === CLOUD_STORAGE.ONE_DRIVE" viewBox="0 0 16 11" width="22" height="16" fill="none">
                  <path d="M6.09859 3.05601L9.45698 5.21557L11.4564 4.31017C11.8625 4.12238 12.2999 4.02513 12.7435 4.02513C12.8185 4.02513 12.8904 4.02849 12.9622 4.03519C12.2812 1.16472 9.55695 -0.565606 6.88273 0.168778C5.67996 0.50076 4.63339 1.30221 3.94922 2.41552H3.9992C4.73961 2.41552 5.46752 2.63685 6.09859 3.05601Z" fill="#0364B8"/>
                  <path d="M6.09854 3.05455C5.46748 2.63538 4.73957 2.41406 3.99916 2.41406H3.94917C1.74045 2.44424 -0.027786 4.38919 0.000330719 6.76001C0.00970297 7.61846 0.25963 8.4568 0.71887 9.16101L3.6805 7.82302L4.99574 7.22948L7.92613 5.9049L9.45694 5.21411L6.09854 3.05455Z" fill="#0078D4"/>
                  <path d="M12.9662 4.0335C12.8943 4.02679 12.8193 4.02344 12.7475 4.02344C12.3039 4.02344 11.8665 4.12068 11.4604 4.30847L9.46094 5.21388L10.042 5.5861L11.9446 6.81007L12.7756 7.34326L15.6123 9.16748C16.462 7.47068 15.8716 5.35807 14.2939 4.44261C13.8784 4.20452 13.4285 4.06703 12.9662 4.0335Z" fill="#1490DF"/>
                  <path d="M12.7683 7.34422L11.9373 6.81104L10.0348 5.58707L9.45369 5.21484L7.92289 5.90563L4.9925 7.23021L3.67726 7.82375L0.71875 9.16174C1.46541 10.3153 2.69005 10.9994 3.99904 10.9994H12.7465C13.9399 10.9994 15.0396 10.2952 15.6081 9.16845L12.7683 7.34422Z" fill="#28A8EA"/>
                </svg>
                <svg v-else-if="storage.provider === CLOUD_STORAGE.BOX_DRIVE" viewBox="0 0 16 9" width="22" height="13">
                  <path d="M8.59631 2.65621C7.40664 2.65621 6.37363 3.33832 5.85161 4.33963C5.32958 3.33828 4.29661 2.65628 3.10698 2.65628C2.40777 2.65628 1.76254 2.89186 1.24339 3.28947V0.620686H1.2431C1.23584 0.276769 0.960655 0 0.621641 0C0.28259 0 0.00748011 0.276769 0.000287697 0.620686H0V5.8771H0.00129464C0.0289135 7.60259 1.40831 8.99307 3.10694 8.99307C4.29657 8.99307 5.32958 8.31085 5.85157 7.3095C6.37363 8.31092 7.40661 8.993 8.59623 8.993C10.3122 8.993 11.7032 7.57435 11.7032 5.82462C11.7033 4.07467 10.3121 2.65621 8.59631 2.65621ZM3.10694 7.72504C2.07767 7.72504 1.24335 6.87419 1.24335 5.82466C1.24335 4.77497 2.07771 3.92417 3.10694 3.92417C4.13622 3.92417 4.97057 4.77497 4.97057 5.82466C4.97057 6.87419 4.13622 7.72504 3.10694 7.72504ZM8.59631 7.72504C7.567 7.72504 6.73271 6.87419 6.73271 5.82458C6.73271 4.7749 7.56703 3.92417 8.59631 3.92417C9.6255 3.92417 10.4599 4.7749 10.4599 5.82458C10.4599 6.87419 9.6255 7.72504 8.59631 7.72504Z" fill="#0075C9"/>
                  <path d="M15.8829 7.99439L14.2917 5.82545L15.8829 3.65648L15.8826 3.65622C16.0794 3.37678 16.0223 2.98698 15.7512 2.77927C15.4802 2.57177 15.0972 2.62447 14.8889 2.89504L14.8887 2.89497L13.5154 4.767L12.1421 2.89497L12.1419 2.8953C11.9335 2.62447 11.5506 2.57177 11.2795 2.77941C11.0083 2.98713 10.9513 3.37692 11.1482 3.6563L11.1479 3.65648L12.7391 5.82545L11.1479 7.99439L11.1482 7.99457C10.9513 8.27401 11.0083 8.66374 11.2795 8.87138C11.5506 9.07909 11.9335 9.02632 12.1419 8.75567L12.1421 8.75593L13.5154 6.88383L14.8887 8.75593L14.8889 8.75586C15.0972 9.02639 15.4802 9.07909 15.7512 8.87152C16.0223 8.66388 16.0794 8.27409 15.8826 7.99468L15.8829 7.99439Z" fill="#0075C9"/>
                </svg>
                <svg v-else viewBox="0 0 16 15" width="20" height="19">
                  <path d="M4.71562 0L0 3.07483L3.28437 5.7315L8 2.773L4.71562 0ZM0 8.31587L4.71562 11.4284L8 8.65542L3.28437 5.7315L0 8.31587ZM8 8.65542L11.3219 11.4284L16 8.35359L12.7563 5.73465C12.7563 5.7315 8 8.65542 8 8.65542ZM16 3.07483L11.3219 0L8 2.773L12.7563 5.73465L16 3.07483ZM8.0375 9.26535L4.71562 12.0384L3.31875 11.0889V12.1515L8.03438 15L12.75 12.1515V11.0889L11.3156 12.0384C11.3219 12.0352 8.0375 9.26535 8.0375 9.26535Z" fill="#007EE5"/>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-[12px] font-semibold text-slate-800 dark:text-slate-100 m-0 truncate">{{ storage.name }}</p>
                <p
                  v-if="storage.authenticationStatus === AUTHENTICATION_STATUS.TOKEN_EXPIRED"
                  class="text-[11px] text-red-500 dark:text-red-400 m-0 truncate"
                >
                  Token expired — click to reconnect
                </p>
                <p v-else-if="storage.cloudEmail" class="text-[11px] text-slate-400 dark:text-slate-500 m-0 truncate">
                  {{ storage.cloudEmail }}
                </p>
              </div>
              <div class="relative shrink-0" @click.stop>
                <button
                  class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer p-0.5"
                  @click="openMenuFor = openMenuFor === storage.id ? null : storage.id"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
                  </svg>
                </button>
                <div
                  v-if="openMenuFor === storage.id"
                  class="absolute top-[calc(100%+4px)] right-0 w-32 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1.5 z-[440] overflow-hidden"
                >
                  <button class="flex items-center w-full px-3 py-2 text-sm text-left border-0 bg-transparent cursor-pointer text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700" @click="onRenameClick(storage)">
                    Rename
                  </button>
                  <button class="flex items-center w-full px-3 py-2 text-sm text-left border-0 bg-transparent cursor-pointer text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30" @click="onRemoveClick(storage)">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ══ Create Project tab ═════════════════════════════════════════════ -->
      <div v-else class="p-5 flex flex-col gap-4">

        <!-- Back hint when arriving from "Create Project +" -->
        <p v-if="returnToDocOnSuccess" class="text-xs text-slate-400 dark:text-slate-500 m-0 -mb-1">
          After creating, you'll return to add your document.
        </p>

        <!-- Project name -->
        <div>
          <label class="block text-sm font-bold text-slate-700 dark:text-slate-200 mb-2">Project name</label>
          <input
            v-model="projectName"
            type="text"
            placeholder="Project name"
            class="w-full bg-slate-100 dark:bg-slate-800 border-0 rounded-xl px-4 py-3 text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900 transition-shadow"
            @keydown.enter="createProject"
          />
        </div>

        <!-- Description -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <label class="text-sm font-bold text-slate-700 dark:text-slate-200">Project description</label>
            <span class="text-sm text-slate-400 dark:text-slate-500">Optional</span>
          </div>
          <textarea
            v-model="projectDescription"
            placeholder="What is this project about? Add context for collaborators..."
            rows="4"
            class="w-full bg-slate-100 dark:bg-slate-800 border-0 rounded-xl px-4 py-3 text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900 transition-shadow resize-none"
          />
        </div>

        <p v-if="createError" class="text-xs text-red-600 dark:text-red-400 m-0">Failed to create the project. Please try again.</p>

        <div class="flex-1 min-h-[60px]" />

        <!-- Actions -->
        <div class="flex items-center justify-end gap-3 pt-2">
          <button
            class="px-6 py-2.5 border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
            @click="close"
          >
            Cancel
          </button>
          <button
            :disabled="!projectName.trim() || isCreating"
            class="px-6 py-2.5 bg-slate-800 dark:bg-slate-700 text-white rounded-xl text-sm font-semibold hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-default"
            @click="createProject"
          >
            {{ isCreating ? "Creating…" : "Create project" }}
          </button>
        </div>
      </div>
    </div>

    <AddStorageModal v-if="showAddStorageModal" @close="showAddStorageModal = false" />

    <CloudStorageFinder
      v-if="selectedStorage"
      :storage="selectedStorage"
      :project-id="targetProjectId()"
      @close="selectedStorage = null"
      @rename="onRenameClick(selectedStorage)"
      @remove="onRemoveClick(selectedStorage)"
      @uploaded="selectedStorage = null; close()"
    />

    <RenameModal
      v-if="renamingStorage"
      title="Rename Storage"
      :initial-name="renamingStorage.name"
      :is-saving="isRenamingStorage"
      @close="renamingStorage = null"
      @save="saveStorageRename"
    />

    <ConfirmModal
      v-if="removingStorage"
      title="Remove Storage?"
      :message="`This disconnects “${removingStorage.name}” — you can reconnect it later, but any documents already imported from it stay in Folia.`"
      confirm-label="Remove"
      busy-label="Removing…"
      :is-busy="isRemovingStorage"
      @close="removingStorage = null"
      @confirm="confirmRemoveStorage"
    />
  </div>
</template>
